import { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

interface Physiotherapist {
  id: number;
  first_name: string;
  last_name: string;
  city?: string;
  phone?: string;
  professional_email?: string;
  full_name: string;
}

interface QuestionnaireRequest {
  id: number;
  specialty_name: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  admin_notes?: string;
  physiotherapist: Physiotherapist;
}

interface Props {
  request: QuestionnaireRequest;
}

const getStatusBadge = (status: string) => {
  const statusConfig = {
    pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    approved: { label: 'Approuvé', className: 'bg-green-100 text-green-800 border-green-300' },
    rejected: { label: 'Rejeté', className: 'bg-red-100 text-red-800 border-red-300' }
  };
  
  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
  
  return (
    <Badge className={config.className}>
      {config.label}
    </Badge>
  );
};

export default function QuestionnaireRequestShow({ request }: Props) {
  const [adminNotes, setAdminNotes] = useState(request.admin_notes || '');

  const handleUpdateNotes = () => {
    router.patch(`/admin/questionnaire_requests/${request.id}`, {
      admin_notes: adminNotes
    }, {
      onSuccess: () => {
        router.reload();
      }
    });
  };

  const handleApprove = () => {
    if (confirm(`Approuver le questionnaire pour ${request.physiotherapist.full_name} ? La spécialité "${request.specialty_name}" sera automatiquement validée.`)) {
      router.post(`/admin/questionnaire_requests/${request.id}/approve`, {}, {
        onSuccess: () => {
          router.visit('/admin/questionnaire_requests');
        }
      });
    }
  };

  const handleReject = () => {
    if (confirm(`Rejeter le questionnaire pour ${request.physiotherapist.full_name} ?`)) {
      router.post(`/admin/questionnaire_requests/${request.id}/reject`, {}, {
        onSuccess: () => {
          router.visit('/admin/questionnaire_requests');
        }
      });
    }
  };

  return (
    <AdminLayout title={`Demande de questionnaire #${request.id}`}>
      <Head title={`Questionnaire #${request.id} - Admin`} />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link href="/admin/questionnaire_requests">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Demande de questionnaire</h1>
              <p className="text-gray-600">#{request.id} - {request.physiotherapist.full_name}</p>
            </div>
          </div>
        </div>

        {/* Status and Actions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3">
                Statut : {getStatusBadge(request.status)}
              </CardTitle>
              {request.status === 'pending' && (
                <div className="flex gap-2">
                  <Button onClick={handleApprove} variant="default" className="bg-green-600 hover:bg-green-700">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approuver
                  </Button>
                  <Button onClick={handleReject} variant="destructive">
                    <XCircle className="h-4 w-4 mr-2" />
                    Rejeter
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Request Info */}
            <div>
              <h3 className="font-semibold mb-2">Informations</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Kinésithérapeute :</strong> {request.physiotherapist.full_name}</p>
                <p><strong>Spécialité :</strong> {request.specialty_name}</p>
                <p><strong>Demandé le :</strong> {new Date(request.created_at).toLocaleString('fr-FR')}</p>
                {request.physiotherapist.phone && (
                  <p><strong>Téléphone :</strong> {request.physiotherapist.phone}</p>
                )}
                {request.physiotherapist.professional_email && (
                  <p><strong>Email professionnel :</strong> {request.physiotherapist.professional_email}</p>
                )}
                {request.physiotherapist.city && (
                  <p><strong>Ville :</strong> {request.physiotherapist.city}</p>
                )}
              </div>
            </div>

            {/* Admin Notes */}
            <div>
              <h3 className="font-semibold mb-2">Notes administrateur</h3>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Ajoutez des notes sur cette demande (lien envoyé, résultats du questionnaire externe, etc.)"
                rows={4}
                className="mb-2"
              />
              <Button onClick={handleUpdateNotes} variant="outline" size="sm">
                Enregistrer les notes
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
